import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * QuestionMedia - Displays images/graphics for quiz questions
 * Supports URL-based images, Base64, and Firebase Storage
 */

// Allowed domains for external images
const ALLOWED_IMAGE_DOMAINS = [
  // Stable public sources
  'upload.wikimedia.org',
  'commons.wikimedia.org',
  'raw.githubusercontent.com',
  
  // Firebase (own storage)
  'firebasestorage.googleapis.com',
  'storage.googleapis.com',
  
  // Other trusted sources
  'images.unsplash.com',
  'cdn.pixabay.com',
  'i.imgur.com'
];

/**
 * Check if URL is from an allowed domain
 */
function isAllowedImageUrl(url) {
  if (!url) return false;
  
  // Allow data URLs (Base64)
  if (url.startsWith('data:image/')) return true;
  
  try {
    const { hostname } = new URL(url);
    return ALLOWED_IMAGE_DOMAINS.some(domain => hostname.endsWith(domain));
  } catch {
    return false;
  }
}

/**
 * Validate Base64 image data
 */
function isValidBase64Image(data) {
  if (!data || typeof data !== 'string') return false;
  
  // Check prefix
  if (!data.startsWith('data:image/')) return false;
  
  // Check allowed types
  const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
  const type = data.split(';')[0].replace('data:', '');
  if (!allowedTypes.includes(type)) return false;
  
  // Check size (estimate) - max 2MB for Base64
  const base64 = data.split(',')[1];
  if (base64) {
    const sizeInBytes = (base64.length * 3) / 4;
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (sizeInBytes > maxSize) return false;
  }
  
  return true;
}

/**
 * Image Loading Skeleton
 */
function ImageSkeleton() {
  return (
    <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg w-full max-w-[600px] mx-auto aspect-video flex items-center justify-center">
      <svg className="w-12 h-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  );
}

/**
 * Image Error State
 */
function ImageError({ message = 'Bild konnte nicht geladen werden' }) {
  return (
    <div className="bg-gray-100 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg w-full max-w-[600px] mx-auto p-8 text-center">
      <svg className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
    </div>
  );
}

/**
 * Placeholder for images that should be added manually
 */
function PlaceholderHint({ description, suggestedSearch }) {
  return (
    <div className="bg-blue-50 dark:bg-blue-900/30 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg w-full max-w-[600px] mx-auto p-6 text-center">
      <svg className="w-10 h-10 mx-auto text-blue-400 dark:text-blue-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
        Bild-Platzhalter
      </p>
      {description && (
        <p className="text-xs text-blue-600 dark:text-blue-400">
          {description}
        </p>
      )}
      {suggestedSearch && (
        <p className="text-xs text-blue-500 dark:text-blue-500 mt-2">
          Suchbegriff: "{suggestedSearch}"
        </p>
      )}
    </div>
  );
}

/**
 * Image Zoom Modal
 */
function ImageZoomModal({ src, alt, onClose }) {
  // Close on ESC
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  
  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Vergrößerte Bildansicht"
    >
      {/* Close Button */}
      <button 
        className="absolute top-4 right-4 text-white text-3xl hover:text-gray-300 theme-transition z-10"
        onClick={onClose}
        aria-label="Schließen"
      >
        ✕
      </button>
      
      {/* Hint */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
        ESC oder Klicken zum Schließen
      </div>
      
      {/* Image */}
      <img 
        src={src} 
        alt={alt || 'Vergrößertes Bild'}
        className="max-w-[95vw] max-h-[95vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

ImageZoomModal.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  onClose: PropTypes.func.isRequired
};

/**
 * Main QuestionMedia Component
 */
export function QuestionMedia({ media, className = '', enableZoom = true }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isZoomed, setIsZoomed] = useState(false);
  
  // Reset state when media changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setErrorMessage('');
  }, [media?.url, media?.data]);
  
  // Handle no media
  if (!media) return null;
  
  // Handle placeholder
  if (media.placeholder) {
    return <PlaceholderHint description={media.description} suggestedSearch={media.suggestedSearch} />;
  }
  
  // Determine image source
  let src = '';
  if (media.source === 'base64' && media.data) {
    if (isValidBase64Image(media.data)) {
      src = media.data;
    } else {
      return <ImageError message="Ungültiges Bildformat" />;
    }
  } else if (media.url) {
    if (isAllowedImageUrl(media.url)) {
      src = media.url;
    } else {
      return <ImageError message="Bild-URL nicht erlaubt" />;
    }
  } else {
    return null;
  }
  
  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };
  
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    setErrorMessage('Bild konnte nicht geladen werden');
  };
  
  const handleZoomClick = useCallback(() => {
    if (enableZoom && !hasError) {
      setIsZoomed(true);
    }
  }, [enableZoom, hasError]);
  
  // Calculate max width style
  const maxWidthStyle = media.maxWidth ? { maxWidth: `${media.maxWidth}px` } : { maxWidth: '600px' };
  
  return (
    <>
      <figure className={`question-media my-4 ${className}`}>
        {/* Loading Skeleton */}
        {isLoading && <ImageSkeleton />}
        
        {/* Error State */}
        {hasError && <ImageError message={errorMessage} />}
        
        {/* Image */}
        {!hasError && (
          <img 
            src={src}
            alt={media.alt || 'Abbildung zur Frage'}
            className={`
              max-w-full mx-auto rounded-lg shadow-sm
              max-h-[50vh] object-contain
              transition-all duration-200
              ${isLoading ? 'opacity-0 absolute' : 'opacity-100'}
              ${enableZoom ? 'cursor-zoom-in hover:shadow-md' : ''}
            `}
            style={maxWidthStyle}
            loading="lazy"
            onLoad={handleLoad}
            onError={handleError}
            onClick={handleZoomClick}
          />
        )}
        
        {/* Caption */}
        {media.caption && !hasError && !isLoading && (
          <figcaption className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center italic">
            {media.caption}
          </figcaption>
        )}
      </figure>
      
      {/* Zoom Modal */}
      {isZoomed && (
        <ImageZoomModal 
          src={src} 
          alt={media.alt} 
          onClose={() => setIsZoomed(false)} 
        />
      )}
    </>
  );
}

QuestionMedia.propTypes = {
  media: PropTypes.shape({
    type: PropTypes.oneOf(['image', 'diagram', 'graph']),
    source: PropTypes.oneOf(['url', 'base64', 'firebase']),
    url: PropTypes.string,
    data: PropTypes.string,
    alt: PropTypes.string,
    caption: PropTypes.string,
    placeholder: PropTypes.bool,
    description: PropTypes.string,
    suggestedSearch: PropTypes.string,
    maxWidth: PropTypes.number
  }),
  className: PropTypes.string,
  enableZoom: PropTypes.bool
};

// Export helpers for use in other components
export { isAllowedImageUrl, isValidBase64Image, ImageSkeleton, ImageError, PlaceholderHint, ImageZoomModal };

export default QuestionMedia;
